import { useEffect, useState } from 'react';
import { useConfig } from '../config/ConfigContext';
import { KeycloakProvider } from '../auth/KeycloakProvider';
import { RouterProvider, useRouter } from './Router';
import TopNav from '../components/TopNav';
import NavRail from '../components/NavRail';
import Wordmark from '../components/Wordmark';
import MarketplacePage from '../pages/MarketplacePage';
import DashboardPage from '../pages/DashboardPage';
import ProfilePage from '../pages/ProfilePage';
import AdminPage from '../pages/AdminPage';
import CartPage, { type CartItem } from '../pages/CartPage';
import AppOperationsPage from '../pages/AppOperationsPage';
import SandboxRequestPage from '../pages/SandboxRequestPage';
import RouteNotFound from '../components/RouteNotFound';
import { isRouteRegistered } from './routes';
import { isAuthenticatedBroker } from '../config/schema';
import { PrefsProvider, usePrefs } from '../prefs/PrefsContext';
import { useTheme } from '../hooks/useTheme';
import { t } from '../i18n';
import type { Route } from './Router';

function ShellContent() {
  const config = useConfig();
  const { route, navigate, previewState } = useRouter();
  const mode = config.instance.mode;

  // brief-28: resolve the theme choice onto <html data-theme> for the whole app.
  const { prefs } = usePrefs();
  useTheme(prefs.theme);

  // SHELL-01: hub-like modes use the persistent left rail as primary navigation
  // instead of TopNav (brief-13-shell-nav-rail). Since the mode split that means
  // every authenticated broker — meta and tenant carry the same chrome; what
  // differs is which routes their mode registers (shell/routes.ts).
  const isHubLike = isAuthenticatedBroker(mode) || mode === 'tier-1-hub';

  // SHELL-05: rail collapses to a drawer <900px, toggled from the mobile bar.
  const [railOpen, setRailOpen] = useState(false);

  // broker_type gate (ADR-446 d2, WP-SL-T4): consumer cart surface is structurally
  // absent (not hidden) in B2B brokers. Default to 'b2b' when absent from config.
  const isB2C = config.instance.broker_type === 'b2c';

  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  function handleAddToCart(item: CartItem) {
    if (!isB2C) return; // defensive: should never be called on B2B
    setCartItems((prev) => {
      const exists = prev.some((i) => i.service_id === item.service_id && i.plan_id === item.plan_id);
      return exists ? prev : [...prev, item];
    });
  }

  function handleRemoveFromCart(service_id: string, plan_id: string) {
    setCartItems((prev) => prev.filter((i) => !(i.service_id === service_id && i.plan_id === plan_id)));
  }

  useEffect(() => {
    document.title = config.instance.name;
  }, [config.instance.name]);

  const isPreview = route.startsWith('/preview/');
  const effectiveRoute: Route = isPreview ? '/' : route;

  // ADR-075 step 5: per-app App Operations Board (operator/tenant-admin surface).
  // No approve control exists on this surface (OQ1b) — approval stays a store action.
  const opsMatch = /^\/apps\/([a-z0-9-]+)\/operations$/.exec(effectiveRoute);

  // decision-2026-07-25-broker-mode-split: registration is by MODE, from one table.
  // An unregistered route resolves to NOT FOUND — never to a role prompt, which would
  // advertise a capability this deployment does not have (adr-075 D11).
  const routeRegistered = isPreview || isRouteRegistered(mode, effectiveRoute);

  const isAdminRoute =
    effectiveRoute === '/admin' ||
    effectiveRoute === '/admin/sub-clients' ||
    effectiveRoute === '/admin/audit-log' ||
    effectiveRoute === '/admin/subscriptions' ||
    effectiveRoute === '/admin/orders' ||
    effectiveRoute === '/admin/sandbox-provisioning';

  const devBanner = import.meta.env.DEV && isPreview && (
    <div
      style={{
        background: 'var(--dp-navy-800)',
        color: 'var(--fg-on-navy)',
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--fs-meta)',
        padding: '4px var(--sp-8)',
        textAlign: 'center',
      }}
    >
      {t('preview.banner')} <strong>{previewState}</strong>
    </div>
  );

  const pageContent = !routeRegistered ? (
    <RouteNotFound route={effectiveRoute} onHome={() => navigate('/')} />
  ) : (
    <>
      {(effectiveRoute === '/' || isPreview) && (
        <MarketplacePage
          previewState={previewState}
          onAddToCart={isB2C ? handleAddToCart : undefined}
          onNavigate={navigate}
        />
      )}
      {/* B2C only: CartPage is structurally absent in B2B brokers (ADR-446 d2) */}
      {isB2C && effectiveRoute === '/cart' && (
        <CartPage
          items={cartItems}
          onClear={() => setCartItems([])}
          onNavigate={navigate}
          onRemove={handleRemoveFromCart}
        />
      )}
      {effectiveRoute === '/dashboard' && <DashboardPage />}
      {effectiveRoute === '/profile' && <ProfilePage />}
      {/* Tenant broker only — registered by mode, not by role (see shell/routes.ts) */}
      {effectiveRoute === '/sandbox/request' && <SandboxRequestPage />}
      {opsMatch && <AppOperationsPage appId={opsMatch[1]} />}
      {isAdminRoute && (
        <AdminPage activePath={effectiveRoute} onNavigate={navigate} />
      )}
    </>
  );

  // SHELL-01: hub-like modes get the persistent left rail; storefront modes
  // (tier-0, reference) keep the unchanged TopNav layout.
  if (isHubLike) {
    return (
      <div className="dp-shell dp-shell--railed">
        <NavRail
          activePath={effectiveRoute}
          onNavigate={navigate}
          cartCount={isB2C ? cartItems.length : 0}
          isB2C={isB2C}
          open={railOpen}
          onClose={() => setRailOpen(false)}
        />

        <div className="dp-shell__main">
          {/* SHELL-05: only visible <900px, opens the rail as an off-canvas drawer */}
          <div className="dp-rail__mobilebar">
            <button
              type="button"
              className="dp-rail__toggle"
              aria-label={railOpen ? t('nav.closeMenu') : t('nav.openMenu')}
              aria-expanded={railOpen}
              onClick={() => setRailOpen((v) => !v)}
            >
              <span aria-hidden="true">☰</span>
            </button>
            <Wordmark />
          </div>

          {devBanner}

          <div className="dp-shell__body">{pageContent}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dp-shell">
      <TopNav activePath={effectiveRoute} onNavigate={navigate} cartCount={isB2C ? cartItems.length : 0} />

      {devBanner}

      <div className="dp-shell__body">{pageContent}</div>
    </div>
  );
}

export default function AppShell() {
  const config = useConfig();

  return (
    <KeycloakProvider config={config}>
      <PrefsProvider>
        <RouterProvider>
          <ShellContent />
        </RouterProvider>
      </PrefsProvider>
    </KeycloakProvider>
  );
}
